import React, {useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Heading,
  useToast,
  Flex,
  Divider,
  Image,
  Stack,
  VStack,
  Text,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { QRCodeCanvas } from 'qrcode.react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useAuth } from '../../../hooks/useAuth';
import axiosInstance from '../../../services/axios';
import Settings from './Settings'; 

const stripePromise = loadStripe('YOUR_STRIPE_PUBLISHABLE_KEY');

const FanProfile = () => {
  const toast = useToast();
  const auth = useAuth();

  // Fan data
  const fanData = auth.user; // e.g., { firstname, email, image_url, etc. }

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Profile image
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(fanData.image_url || '');

  // Email Change (OTP)
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  // Password Change
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pLoading, setPLoading] = useState(false);

  // SMS & MFA
  const [smsConsent, setSmsConsent] = useState(auth.user.isSMSenabled || false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  /* --------------------------------------
     1) PROFILE: Name & Profile Image
  -------------------------------------- */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Optionally handle actual image upload separately
  const handleImageUpload = async () => {
    if (!imageFile) return fanData.image_url; // no new file

    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const response = await axiosInstance.post(
        `/music/upload/file?fan=${fanData.firstname}`, 
        formData
      );
      return response.data; // should be the new image URL
    } catch (error) {
      toast({
        title: 'Error Uploading Image',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const onSubmitProfile = async (values) => {
    try {
      const { firstname , lastname} = values;
 
      // Upload image if needed
      const newImageUrl = await handleImageUpload();

      // Update names first
      await axiosInstance.put('/users/update/user/names', {
        firstname,
        lastname,
      });

      // Update image if changed
      if (newImageUrl && newImageUrl !== fanData.image_url) {
        await axiosInstance.put('/users/update/user', {
          image_url: newImageUrl,
        });
      }

      toast({
        title: 'Profile updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      reset();
    } catch (error) {
      toast({
        title: 'Error updating profile',
        description: error?.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /* --------------------------------------
     2) EMAIL CHANGE (with OTP)
  -------------------------------------- */
  const sendOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await axiosInstance.get('users/profile/send_otp');
      if (response.data) {
        setOtpSent(true);
        toast({
          title: 'OTP sent successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error sending OTP',
        description: error?.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    if (!newEmail || !otp) return;

    try {
      setOtpLoading(true);
      const response = await axiosInstance.post('users/profile/update_email/', {
        new_email: newEmail,
        otp,
      });

      toast({
        title: response.data,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Usually you'd refresh or log out if the email changed
      auth.logout();
    } catch (error) {
      toast({
        title: 'Error changing email',
        description: error?.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpSent(false);
      setNewEmail('');
      setOtp('');
      setOtpLoading(false);
    }
  };

  /* --------------------------------------
     3) CHANGE PASSWORD
  -------------------------------------- */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setPLoading(true);
      await axiosInstance.post('users/reset/password/', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast({
        title: 'Password reset successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Log out after changing password
      auth.logout();
    } catch (error) {
      toast({
        title: 'Error resetting password',
        description: error?.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    }
  };

  /* --------------------------------------
     4) SMS OPT-IN & MFA
  -------------------------------------- */
  const handleSmsConsentChange = async () => {
    try {
      const newSmsConsent = !fanData.isSMSenabled;
      await axiosInstance.put(`/users/update/sms/option?update=${newSmsConsent}`);
      // Update local state
      setSmsConsent(newSmsConsent);

      toast({
        title: 'SMS consent updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating SMS consent',
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEnableAuthenticator = async () => {
    try {
      const response = await axiosInstance.get('/auth/qr-code');
      setQrCodeUrl(response.data.data);

      toast({
        title: 'Authenticator QR code generated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error generating QR code',
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box py={8} px={4} bg="dark.100">
      <Flex direction="column" alignItems="center">
        <Heading color="dark.900" mb={6}>
          Settings
        </Heading>

        {/** 1) PROFILE SECTION */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Profile
          </Text>
          <form onSubmit={handleSubmit(onSubmitProfile)}>
            <Stack spacing={6}>
              {/* First Name */}
              <FormControl isInvalid={errors.firstname}>
                <FormLabel color="primary.100">First Name</FormLabel>
                <Input
                  placeholder="First Name"
                  variant="filled"
                  defaultValue={fanData.firstname}
                  {...register('firstname', { required: 'First name is required' })}
                />
                <FormErrorMessage>
                  {errors.firstname && errors.firstname.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.lastname}>
                <FormLabel color="primary.100">Last Name</FormLabel>
                <Input
                  placeholder="First Name"
                  variant="filled"
                  defaultValue={fanData.lastname}
                  {...register('lastname', { required: 'Last name is required' })}
                />
                <FormErrorMessage>
                  {errors.lastname && errors.lastname.message}
                </FormErrorMessage>
              </FormControl>

              {/* Profile Image */}
              <FormControl>
                <FormLabel color="primary.100">Profile Image</FormLabel>
                {imagePreview && (
                  <Image
                    src={imagePreview}
                    alt="Profile Image"
                    boxSize="200px"
                    objectFit="cover"
                    mb={4}
                  />
                )}
                <Flex>
                  <Input
                    type="file"
                    accept="image/*"
                    display="none"
                    id="file-upload"
                    onChange={handleImageChange}
                  />
                  <Button as="label" htmlFor="file-upload" variant="solid">
                    Choose File
                  </Button>
                </Flex>
              </FormControl>

              <Button type="submit">
                Update Profile
              </Button>
            </Stack>
          </form>
        </Box>

        <Divider mb={8} />

        {/** 2) CURRENT EMAIL */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} color="dark.900">
            Current Email
          </Text>
          <Text mb={4} color="primary.900">
            {fanData.email}
          </Text>
          <Button
            colorScheme="teal"
            onClick={() => setIsEmailFormVisible(!isEmailFormVisible)}
          >
            {isEmailFormVisible ? 'Cancel' : 'Change Email'}
          </Button>

          {isEmailFormVisible && (
            <Box mt={4}>
              {!otpSent ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                >
                  <FormControl isRequired>
                    <FormLabel color="primary.500">New Email</FormLabel>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Send OTP
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleChangeEmail}>
                  <FormControl isRequired>
                    <FormLabel color="primary.500">Enter OTP</FormLabel>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Change Email
                  </Button>
                </form>
              )}
            </Box>
          )}
        </Box>

        <Divider mb={8} />

        {/** 3) CHANGE PASSWORD */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Password
          </Text>
          <Button
            colorScheme="teal"
            onClick={() => setIsPasswordFormVisible(!isPasswordFormVisible)}
          >
            {isPasswordFormVisible ? 'Cancel' : 'Change Password'}
          </Button>

          {isPasswordFormVisible && (
            <VStack spacing={4} align="flex-start" w="100%" mt={4}>
              <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
                <FormControl isRequired>
                  <FormLabel color="primary.500">Old Password</FormLabel>
                  <Input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.500">New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.500">Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="green"
                  mt={4}
                  w="full"
                  isLoading={pLoading}
                >
                  Change Password
                </Button>
              </form>
            </VStack>
          )}
        </Box>

        <Divider mb={8} />

   
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Payment Settings
          </Text>
         
          <Elements stripe={stripePromise}>
            <Settings />
          </Elements>
        </Box>

        <Divider mb={8} />

        {/** 5) ADDITIONAL SECURITY (SMS + MFA) */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Additional Security
          </Text>

          {/* SMS Opt-in */}
          <FormControl display="flex" alignItems="center" mb={6}>
            <FormLabel mb="0" color="primary.500">
              Opt-in for SMS Notifications?
            </FormLabel>
            <Switch
              isChecked={smsConsent}
              onChange={handleSmsConsentChange}
              colorScheme="teal"
            />
          </FormControl>

          {/* MFA QR Code */}
          <Button onClick={handleEnableAuthenticator} colorScheme="teal">
            View MFA QR Code
          </Button>
          {qrCodeUrl && (
            <Box mt={4}>
              <QRCodeCanvas value={qrCodeUrl} size={200} />
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default FanProfile;